/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionKernelType, ActionType } from 'client-code/actions';
import {
    StationPolicy,
    StreamingDeviceType,
    StreamingRestrictionsInternal,
} from 'client-code/channels-service';

import { DeviceModel } from '../../device';
import { PCState } from '../../enums';
import { ServicesClientImpl } from '../../interactors';
import { PlaybackContentType } from '../../playback/enums';
import { TMetadata } from '../../types';
import { CcbSwitch } from '../../utils/ccb';
import { CcbSwitchName } from '../../utils/ccb/enums';
import { PlaybackRestrictionType } from '../enums';
import { IMetadata, IPlayable, IRestriction } from '../interfaces';
import { TMetadataRestrictionParams } from '../types';

/* eslint-disable class-methods-use-this */
export class MetadataRestrictionImpl implements IRestriction<TMetadataRestrictionParams> {
    private checkDeviceTypeRestriction(
        policyInfo: Array<StationPolicy | undefined>,
        playbackContentType: PlaybackContentType,
        metadata: IMetadata | undefined,
    ): boolean {
        if (
            playbackContentType === PlaybackContentType.NDVR_LIVE ||
            playbackContentType === PlaybackContentType.NDVR_RECORDED
        ) {
            return this.checkDeviceTypeRestrictionForNDVR(metadata);
        }

        if (!policyInfo?.length) return false;

        if (playbackContentType === PlaybackContentType.LIVE) {
            return this.checkDeviceTypeRestrictionForLive(policyInfo, playbackContentType);
        }
        return this.checkDeviceTypeRestrictionForSoCu(policyInfo);
    }

    private checkDeviceTypeRestrictionForLive(
        policyInfo: Array<StationPolicy | undefined>,
        playbackContentType: PlaybackContentType,
    ): boolean {
        const deviceType = <StreamingDeviceType>DeviceModel.getTiVoSettings().getDeviceType();

        const filteredPolicies: StreamingRestrictionsInternal[] = this.getFilteredStationPolicies(
            policyInfo,
            playbackContentType,
        );

        for (let _index = 0; _index < filteredPolicies.length; _index += 1) {
            const streamingRestrictions = filteredPolicies[_index];

            if (
                streamingRestrictions?.restrictedDeviceType?.includes(deviceType) &&
                !streamingRestrictions.checkDeviceTypeWithMso
            )
                return true;
        }

        return false;
    }

    private checkDeviceTypeRestrictionForNDVR(metadata: IMetadata | undefined): boolean {
        if (metadata) {
            let watchAction = metadata.actionList.actions.find((action) => {
                return (
                    action.actionType === ActionType.WatchNowAction &&
                    action.kernel.actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel
                );
            });
            if (watchAction) {
                return watchAction.kernel.isRestrictedByDeviceType;
            }
        }
        return false;
    }

    private checkDeviceTypeRestrictionForSoCu(
        policyInfo: Array<StationPolicy | undefined>,
    ): boolean {
        if (policyInfo && policyInfo.length > 0) {
            for (let i = 0; i < policyInfo.length; i += 1) {
                const streamingRules = policyInfo[i]?.linearStationRules?.streamingRules;
                const soStreamingRestrictions = streamingRules?.soStreamingRestrictions;
                const cuStreamingRestrictions = streamingRules?.cuStreamingRestrictions;
                if (
                    this.isDeviceRestricted(soStreamingRestrictions) &&
                    this.isDeviceRestricted(cuStreamingRestrictions)
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    private checkDeviceTypeRestrictionForVOD(metadata: IMetadata | undefined): boolean {
        return metadata?.watchVodAction?.kernel?.isStreamingRestrictedByDeviceType || false;
    }

    private isDeviceRestricted(
        streamingRestrictions: StreamingRestrictionsInternal | undefined,
    ): boolean {
        if (
            streamingRestrictions?.restrictedDeviceType?.includes(
                <StreamingDeviceType>DeviceModel.getTiVoSettings().getDeviceType(),
            ) &&
            !streamingRestrictions.checkDeviceTypeWithMso
        ) {
            return true;
        }
        return false;
    }

    private checkIsAdult(metadata: IMetadata): boolean {
        return this.checkPCViolations(metadata);
    }

    private checkPCViolations(metadata: IMetadata): boolean {
        const services = new ServicesClientImpl();
        const pcState = services.getPCState();

        return pcState === PCState.Off ? false : services.checkPCViolations(metadata as TMetadata);
    }

    // TODO: move to utils
    private getFilteredStationPolicies(
        policyInfo: Array<StationPolicy | undefined>,
        playbackContentType: PlaybackContentType,
    ): StreamingRestrictionsInternal[] {
        let filteredPolicies: StreamingRestrictionsInternal[] = [];

        switch (playbackContentType) {
            default:
                filteredPolicies = policyInfo
                    .filter((policy) => {
                        return policy?.linearStationRules?.streamingRules
                            ?.linearStreamingRestrictions;
                    })
                    .map((policy) => {
                        return policy?.linearStationRules?.streamingRules
                            ?.linearStreamingRestrictions!;
                    });
        }
        return filteredPolicies;
    }

    public checkRestriction(data: {
        playable: IPlayable;
        metadata?: IMetadata;
    }): PlaybackRestrictionType {
        const { playable, metadata } = data;
        const { channel, playbackContentType } = playable;
        if (
            channel &&
            channel.policyInfo &&
            this.checkDeviceTypeRestriction(channel.policyInfo, playbackContentType, metadata)
        )
            return PlaybackRestrictionType.DEVICE_RESTRICTED;

        if (
            (playbackContentType === PlaybackContentType.NDVR_LIVE ||
                playbackContentType === PlaybackContentType.NDVR_RECORDED) &&
            this.checkDeviceTypeRestrictionForNDVR(metadata)
        )
            return PlaybackRestrictionType.DEVICE_RESTRICTED;

        if (
            playbackContentType === PlaybackContentType.VOD &&
            metadata &&
            this.checkDeviceTypeRestrictionForVOD(metadata)
        )
            return PlaybackRestrictionType.DEVICE_RESTRICTED;

        if (channel && !channel.isSubscribed) return PlaybackRestrictionType.UNSUBSCRIBED;

        if (metadata && this.checkIsAdult(metadata)) return PlaybackRestrictionType.ADULT;

        return PlaybackRestrictionType.NO_RESTRICTION;
    }
}
